import { supabase } from '@/integrations/supabase/client';

/**
 * First-run helpers used to reduce onboarding friction.
 *
 * Goal: a brand new user should reach a usable home screen before any
 * modal interrupts them, and never see more than one prompt per session.
 */

const FIRST_SESSION_KEY = 'luvall-first-session-seen';
const SESSION_PROMPT_KEY = 'luvall-session-prompt-shown';

/** True the very first time this browser opens the signed-in app. */
export const isFirstSession = (): boolean => {
  try {
    return localStorage.getItem(FIRST_SESSION_KEY) !== '1';
  } catch {
    return false;
  }
};

/** Mark the first session as seen so deferred prompts can appear later. */
export const markFirstSessionSeen = (): void => {
  try {
    localStorage.setItem(FIRST_SESSION_KEY, '1');
  } catch {
    /* ignore */
  }
};

export type PromptId = 'profile' | 'skill' | 'notifications' | 'install' | 'tour';

/**
 * Only one interruption per browser session. Returns true when the caller
 * owns the session's prompt slot (either it already claimed it, or it is free).
 */
export const claimSessionPrompt = (id: PromptId): boolean => {
  try {
    const current = sessionStorage.getItem(SESSION_PROMPT_KEY);
    if (current === id) return true;
    if (current) return false;
    sessionStorage.setItem(SESSION_PROMPT_KEY, id);
    return true;
  } catch {
    return true;
  }
};

/** Read-only check: is the session prompt slot free (or already ours)? */
export const canShowPrompt = (id: PromptId): boolean => {
  try {
    const current = sessionStorage.getItem(SESSION_PROMPT_KEY);
    return !current || current === id;
  } catch {
    return true;
  }
};

const DASHBOARD_TOUR_KEY = 'luvall-dashboard-tour-done';
const DASHBOARD_TOUR_REPLAY_KEY = 'luvall-dashboard-tour-replay-requested';

const getDashboardTourKey = (userId?: string | null) =>
  userId ? `${DASHBOARD_TOUR_KEY}:${userId}` : DASHBOARD_TOUR_KEY;

/** Has this browser already seen (or skipped) the dashboard walkthrough? */
export const hasLocalDashboardTourDone = (userId?: string | null): boolean => {
  try {
    return localStorage.getItem(getDashboardTourKey(userId)) === '1';
  } catch {
    return false;
  }
};

/** Has the user already seen (or skipped) the dashboard walkthrough? */
export const isDashboardTourDone = (completedAt?: string | null, userId?: string | null): boolean => {
  if (completedAt) return true;
  return hasLocalDashboardTourDone(userId);
};

/** Mark the dashboard walkthrough as seen so it does not auto-start again. */
export const markDashboardTourDone = async (userId?: string | null): Promise<string> => {
  const completedAt = new Date().toISOString();

  try {
    localStorage.setItem(getDashboardTourKey(userId), '1');
    if (!userId) localStorage.setItem(DASHBOARD_TOUR_KEY, '1');
  } catch {
    /* ignore */
  }

  if (userId) {
    const { error } = await supabase
      .from('profiles')
      .update({
        dashboard_tour_completed_at: completedAt,
        updated_at: completedAt,
      })
      .eq('id', userId);

    if (error) {
      console.error('Failed to persist dashboard tour completion:', error);
    }
  }

  return completedAt;
};

/** Queue an intentional one-time replay of the dashboard walkthrough. */
export const requestDashboardTourReplay = (): void => {
  try {
    sessionStorage.setItem(DASHBOARD_TOUR_REPLAY_KEY, '1');
    sessionStorage.removeItem(SESSION_PROMPT_KEY);
  } catch {
    /* ignore */
  }
};

/** Is there an intentional one-time replay request? */
export const isDashboardTourReplayRequested = (): boolean => {
  try {
    return sessionStorage.getItem(DASHBOARD_TOUR_REPLAY_KEY) === '1';
  } catch {
    return false;
  }
};

/** Clear the intentional replay request after the tour opens/closes. */
export const clearDashboardTourReplayRequest = (): void => {
  try {
    sessionStorage.removeItem(DASHBOARD_TOUR_REPLAY_KEY);
  } catch {
    /* ignore */
  }
};
